// Consumer complaints and urgent safety reports.
//
// Two queues, two clocks, deliberately not merged. The complaint screen
// repeats that ConfirmPH decides no legal liability and refunds no rental
// money; the safety screen repeats that an allegation is not a finding and
// that a restriction is temporary and reviewable.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import {
  CAP, can, fmtDateTime, humanise, legalError, plGet, plPost,
} from "../../lib/platformLegal";
import {
  Button, ClockPill, Empty, Field, Loading, Modal, Note, Section, SeverityPill, Stat, Table,
  inputClass,
} from "./ui";

export default function CasesSection({ capabilities, mode = "complaints" }) {
  return mode === "complaints"
    ? <Complaints capabilities={capabilities} />
    : <SafetyReports capabilities={capabilities} />;
}

// ---------------------------------------------------------------------------
// Complaints
// ---------------------------------------------------------------------------
function Complaints({ capabilities }) {
  const [data, setData] = useState(null);
  const [queue, setQueue] = useState("");
  const [open, setOpen] = useState(null);
  const [loading, setLoading] = useState(true);
  const writable = can(capabilities, CAP.complaints);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await plGet("/complaints", { params: queue ? { queue } : {} }));
    } catch (err) {
      toast.error(legalError(err));
    } finally {
      setLoading(false);
    }
  }, [queue]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <Loading label="Reading the complaint queue..." />;
  if (!data) return <Empty>The complaint queue could not be loaded.</Empty>;

  const board = data.dashboard;

  return (
    <div className="space-y-4">
      <Section testId="pl-complaint-board" title="Consumer complaints"
               description={board.note}>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          <Stat label="Open" value={board.open} testId="pl-complaints-open" />
          <Stat label="Overdue" value={board.overdue}
                tone={board.overdue ? "text-rose-700" : "text-slate-900"} />
          <Stat label="Due 24h" value={board.due_24h} />
          <Stat label="Due soon" value={board.due_soon} />
          <Stat label="Urgent" value={board.urgent} />
          <Stat label="Escalated" value={board.escalated} />
        </div>
        <Note tone="amber" testId="pl-liability-note">{board.liability_note}</Note>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {["", "overdue", "due_24h", "due_soon", "urgent"].map((key) => (
            <button key={key || "all"} onClick={() => setQueue(key)}
                    data-testid={`pl-complaint-queue-${key || "all"}`}
                    className={`rounded-lg border px-3 py-1.5 text-xs font-medium ${
                      queue === key ? "border-slate-900 bg-slate-900 text-white"
                                    : "border-slate-200 bg-white text-slate-700"}`}>
              {key ? humanise(key) : "All open"}
            </button>
          ))}
        </div>
      </Section>

      <Section testId="pl-complaints">
        {data.complaints.length === 0 ? (
          <Empty testId="pl-complaints-empty">Nothing in this queue.</Empty>
        ) : (
          <Table head={["Reference", "Category", "Business", "Due", "Status"]}
                 testId="pl-complaint-table" minWidth={640}>
            {data.complaints.map((row) => (
              <tr key={row.id} data-testid={`pl-complaint-${row.reference}`}>
                <td className="py-2 pr-3">
                  <button onClick={() => setOpen(row.id)}
                          className="font-mono text-xs text-slate-900 hover:underline">
                    {row.reference}
                  </button>
                  <div className="text-[11px] text-slate-500">{fmtDateTime(row.received_at)}</div>
                </td>
                <td className="py-2 pr-3 text-xs">
                  {humanise(row.category)}
                  <div><SeverityPill severity={row.priority} /></div>
                </td>
                <td className="py-2 pr-3 text-xs">{row.company_name || "—"}</td>
                <td className="py-2 pr-3"><ClockPill clock={row.clock} /></td>
                <td className="py-2 pr-3 text-xs">{humanise(row.status)}</td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      {open && (
        <ComplaintModal complaintId={open} writable={writable} options={data}
                        onClose={() => setOpen(null)}
                        onChanged={load} />
      )}
    </div>
  );
}

function ComplaintModal({ complaintId, writable, options, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setDetail(await plGet(`/complaints/${complaintId}`));
    } catch (err) {
      toast.error(legalError(err));
    }
  }, [complaintId]);

  useEffect(() => { load(); }, [load]);

  const act = async (path, body, message) => {
    setBusy(true);
    try {
      await plPost(`/complaints/${complaintId}/${path}`, body);
      toast.success(message);
      await load();
      onChanged();
    } catch (err) {
      toast.error(legalError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!detail) return <Modal title="Complaint" onClose={onClose}><Loading /></Modal>;
  const complaint = detail.complaint;

  return (
    <Modal title={`Complaint ${complaint.reference}`} onClose={onClose}
           testId="pl-complaint-modal"
           description={`${humanise(complaint.category)} · ${complaint.company_name || "no business"}`}>
      <div className="flex flex-wrap items-center gap-2">
        <ClockPill clock={complaint.clock} />
        <SeverityPill severity={complaint.priority} />
        <span className="text-xs text-slate-600">{humanise(complaint.status)}</span>
      </div>
      <p className="text-sm text-slate-800 whitespace-pre-wrap">{complaint.description}</p>
      {complaint.requested_remedy && (
        <Field label="Requested remedy">
          <p className="text-sm text-slate-700">{complaint.requested_remedy}</p>
        </Field>
      )}
      <Note tone="amber">{complaint.liability_note}</Note>

      {writable && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Button variant="secondary" busy={busy}
                  onClick={() => act("acknowledge", {}, "Acknowledged.")}>
            Acknowledge
          </Button>
          <Button variant="secondary" busy={busy} onClick={() => {
            const note = window.prompt("Record what the business was told:");
            if (note) act("notify-merchant", { note }, "Merchant notified.");
          }}>Notify merchant</Button>
          <Button variant="secondary" busy={busy} onClick={() => {
            const note = window.prompt("Record the merchant's response:");
            if (note) act("merchant-response", { note }, "Response recorded.");
          }}>Record response</Button>
          <Button variant="secondary" busy={busy} onClick={() => {
            const action = window.prompt(
              `Platform action (${(options.platform_actions || []).join(", ")}):`);
            if (!action) return;
            const note = window.prompt("What was actually done?");
            if (note) act("platform-action", { action, note }, "Action recorded.");
          }}>Record platform action</Button>
          <Button variant="secondary" busy={busy} onClick={() => {
            const outcome = window.prompt(`Outcome (${(options.outcomes || []).join(", ")}):`);
            if (!outcome) return;
            const note = window.prompt("What happened, and what was the complainant told?");
            if (note) act("resolve", { outcome, note }, "Resolved.");
          }}>Resolve</Button>
          <Button variant="secondary" busy={busy} onClick={() => {
            const reason = window.prompt("Why is this being reopened?");
            if (reason) act("reopen", { reason }, "Reopened.");
          }}>Reopen</Button>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 p-3">
        <div className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
          Event history
        </div>
        <ul className="mt-2 space-y-1.5 text-xs">
          {detail.events.map((event) => (
            <li key={event.id} className="text-slate-700">
              <span className="font-medium">{humanise(event.action)}</span>{" "}
              <span className="text-slate-500">{fmtDateTime(event.at)} · {event.by_name}</span>
              {event.note && <div className="text-slate-600">{event.note}</div>}
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Urgent safety reports
// ---------------------------------------------------------------------------
function SafetyReports({ capabilities }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [loading, setLoading] = useState(true);
  const writable = can(capabilities, CAP.safety);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await plGet("/safety-reports"));
    } catch (err) {
      toast.error(legalError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <Loading label="Reading the safety queue..." />;
  if (!data) return <Empty>The safety queue could not be loaded.</Empty>;

  const board = data.dashboard;

  return (
    <div className="space-y-4">
      <Section testId="pl-safety-board" title="Urgent safety reports"
               description="Credible, immediate physical-safety allegations. Triaged first,
                            answered fast, and kept separate from ordinary complaints.">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          <Stat label="Open" value={board.open} testId="pl-safety-open" />
          <Stat label="Untriaged" value={board.untriaged}
                tone={board.untriaged ? "text-rose-700" : "text-slate-900"} />
          <Stat label="Overdue" value={board.overdue}
                tone={board.overdue ? "text-rose-700" : "text-slate-900"} />
          <Stat label="Critical" value={board.critical} />
          <Stat label="With holds" value={board.with_holds} />
        </div>
        <Note tone="amber" testId="pl-due-process-note">{board.due_process_note}</Note>
      </Section>

      <Section testId="pl-safety-reports">
        {data.reports.length === 0 ? (
          <Empty testId="pl-safety-empty">Nothing in the queue.</Empty>
        ) : (
          <Table head={["Reference", "Allegation", "Business", "Triage due", "Status"]}
                 testId="pl-safety-table" minWidth={660}>
            {data.reports.map((row) => (
              <tr key={row.id} data-testid={`pl-safety-${row.reference}`}>
                <td className="py-2 pr-3">
                  <button onClick={() => setOpen(row)}
                          className="font-mono text-xs text-slate-900 hover:underline">
                    {row.reference}
                  </button>
                  <div className="text-[11px] text-slate-500">{fmtDateTime(row.reported_at)}</div>
                </td>
                <td className="py-2 pr-3 text-xs">
                  {humanise(row.allegation)}
                  <div><SeverityPill severity={row.severity} /></div>
                </td>
                <td className="py-2 pr-3 text-xs">{row.company_name || "—"}</td>
                <td className="py-2 pr-3"><ClockPill clock={row.clock} /></td>
                <td className="py-2 pr-3 text-xs">
                  {humanise(row.status)}
                  {row.safety_hold_id && (
                    <div className="text-[11px] text-rose-700">Safety hold open</div>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      {open && (
        <SafetyModal report={open} writable={writable} options={data}
                     onClose={() => setOpen(null)} onChanged={load} />
      )}
    </div>
  );
}

function SafetyModal({ report, writable, options, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(report);

  const act = async (path, body, message) => {
    setBusy(true);
    try {
      setDetail(await plPost(`/safety-reports/${report.id}/${path}`, body));
      toast.success(message);
      onChanged();
    } catch (err) {
      toast.error(legalError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Safety report ${detail.reference}`} onClose={onClose}
           testId="pl-safety-modal"
           description={`${humanise(detail.allegation)} · ${detail.company_name || "no business"}`}>
      <div className="flex flex-wrap items-center gap-2">
        <ClockPill clock={detail.clock} />
        <SeverityPill severity={detail.severity} />
        <span className="text-xs text-slate-600">{humanise(detail.status)}</span>
      </div>
      <p className="text-sm text-slate-800 whitespace-pre-wrap">{detail.description}</p>
      <Note tone="amber">{detail.due_process_note}</Note>

      {detail.hold_unavailable_reason && (
        <Note tone="rose" testId="pl-hold-unavailable">{detail.hold_unavailable_reason}</Note>
      )}

      {writable && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Button variant="secondary" busy={busy} onClick={() => {
            const outcome = window.prompt(
              `Triage outcome (${(options.triage_outcomes || []).join(", ")}):`);
            if (!outcome) return;
            const note = window.prompt("What was checked, and what did it show?");
            if (note) act("triage", { outcome, note }, "Triaged.");
          }} data-testid="pl-safety-triage">Triage</Button>

          <Button variant="secondary" busy={busy} disabled={!detail.triaged_at} onClick={() => {
            const reason = window.prompt(
              "Record the specific safety concern the restriction is based on:");
            if (reason) act("request-hold", { reason }, "Safety hold requested.");
          }} data-testid="pl-safety-hold">Restrict vehicle</Button>

          <Button variant="secondary" busy={busy} onClick={() => {
            const note = window.prompt("Record what the business was told:");
            if (note) act("notify-business", { note }, "Business notified.");
          }}>Notify business</Button>

          <Button variant="secondary" busy={busy} onClick={() => {
            const resolution = window.prompt(
              `Resolution (${(options.resolutions || []).join(", ")}):`);
            if (!resolution) return;
            const note = window.prompt("Basis for closing this report:");
            if (note) act("resolve", { resolution, note }, "Report closed.");
          }}>Resolve</Button>
        </div>
      )}

      {!detail.triaged_at && (
        <Note tone="slate">
          A vehicle cannot be restricted before triage: an unassessed allegation must not restrict
          a business.
        </Note>
      )}
      {detail.safety_hold_id && (
        <Note tone="slate">
          A safety hold is linked to this report. Holds are released through Enhanced Vehicle
          Safety's own evidence review — closing the report does not release it.
        </Note>
      )}
    </Modal>
  );
}
